<template>
	<div id="com_mod_risk_measures" class="ui-widget-container app">
		<div class="com-mod-module-header-container">
			<div>
				<h3 class="app-title">Risk Measures</h3>
			</div>
		</div>
		<div class="row">
			<div class="col-md-12">
				<div class="risk-measures">
					<table>
						<tbody>
							<tr class="row-container">
								<th scope="col" />
								<th scope="col" />
								<th scope="col" colspan="2" class="com-mod-table-data align-right pull-right">Fund</th>
							</tr>
							<tr class="row-container">
								<td class="com-mod-table-data align-left" colspan="2">
									<sticky-text-wrap
										:text-content="[{ content: ['Standard'] }, { noWrap: true, content: ['Deviation', { slotName: 'stdDisclosure' }, { slotName: 'SDFIBall' }] }]"
									>
										<template #stdDisclosure>
											<disclosure-superscript
												class="detail-page"
												anchor="riskMeasures_StandardDeviation"
												:statistics="[`stdDeviation`]"
											/>
										</template>
										<template #SDFIBall>
											<i-ball-modal tag="SDF" />
										</template>
									</sticky-text-wrap>
								</td>
								<td class="align-right com-mod-table-data">{{ $format.formatExtraZero(mstarBenchMark.standardDeviation) }}</td>
							</tr>
							<tr class="row-container">
								<td class="com-mod-table-data align-left" colspan="2">
									<sticky-text-wrap
										:text-content="[{ content: ['Sharpe'] }, { noWrap: true, content: ['Ratio', { slotName: 'sharpeDisclosure'}, { slotName: 'SRATIBall' } ] }]"
									>
										<template #sharpeDisclosure>
											<disclosure-superscript
												class="detail-page"
												anchor="riskMeasures_sharpe"
												:statistics="[`sharpe`]"
											/>
										</template>
										<template #SRATIBall>
											<i-ball-modal tag="SRAT" />
										</template>
									</sticky-text-wrap>
								</td>
								<td class="align-right com-mod-table-data">{{ $format.formatExtraZero(mstarBenchMark.sharpeRatio) }}</td>
							</tr>
							<tr class="row-container" style="border-bottom: none;">
								<td
									class="sub-info adjust-date"
									colspan="2"
								>
									For the {{ mstarBenchMark.mStarRiskMeasureYear|| '--' }} Years ending {{ $format.shortDate(mstarBenchMark.asOfDate) }} (updated monthly).
								</td>
							</tr>
							<tr class="row-container" style="border-bottom: none;">
								<td class="com-mod-table-data align-left" />
								<td v-if="isMultipleIndex" class="align-right com-mod-table-data">
									<span>
										<sticky-text-wrap
											:text-content="[{content: ['Insurance Series']}, {noWrap: true, content: [' Benchmark', {slotName: 'superscript1'}]}]"
										>
											<template #superscript1>
												<disclosure-superscript
													class="detail-page"
													anchor="riskMeasures_AFbenchmark"
													:statistics="['rSquared', 'beta', 'captureDown', 'captureUp']"
												/>
											</template>
										</sticky-text-wrap>
									</span>
								</td>
								<td class="align-right com-mod-table-data" :colspan="isMultipleIndex ? '1' : '2'">
									<span>
										<sticky-text-wrap
											:text-content="[{content: [`${columnHeader.substring(0, columnHeader.lastIndexOf(' '))}`]}, {noWrap: true, content: [` ${columnHeader.split(' ').pop()}`, {slotName: 'superscript1'}]}]"
										>
											<template #superscript1>
												<disclosure-superscript
													class="detail-page"
													anchor="riskMeasures_MSbenchmark"
													:statistics="['NF_MS_RSQ_AFR', 'NF_MS_BETA_AF', 'NF_MS_DCAPRAT', 'NF_MS_UCAPRAT']"
												/>
											</template>
										</sticky-text-wrap>
									</span>
								</td>
							</tr>
							<tr class="row-container">
								<th
									class="com-mod-table-data align-right"
									style="vertical-align: top;"
									colspan="2"
									scope="col"
								>
									<sticky-text-wrap
										v-if="isMultipleIndex"
										:text-content="[{ content: [cgBenchMarkName[0]] }, { noWrap: true, content: [cgBenchMarkName[1], { slotName:'CGIndexDisclosure' }, { slotName: 'CGIndexIBall' }]}]"
									>
										<template #CGIndexDisclosure>
											<disclosure-superscript
												class="detail-page"
												anchor="riskMeasureBenchmarkIndex"
												:statistics="[`${cgBenchMark.indexID}`]"
											/>
										</template>
										<template #CGIndexIBall>
											<i-ball-modal :tag="cgIndexId" />
										</template>
									</sticky-text-wrap>
								</th>
								<th class="align-right com-mod-table-data" scope="col">
									<sticky-text-wrap
										:text-content="[{ content: [mstarBenchMarkkName[0]] }, { noWrap: true, content: [mstarBenchMarkkName[1], { slotName:'CGIndexDisclosure' }, { slotName: 'CGIndexIBall' }, { slotName: 'MB_STRATEGY' }] }]"
									>
										<template #CGIndexDisclosure v-if="!isMultipleIndex">
											<disclosure-superscript
												class="detail-page"
												anchor="riskMeasureBenchmarkIndex"
												:statistics="[`${cgBenchMark.indexID}`]"
											/>
										</template>
										<template #CGIndexIBall v-if="!isMultipleIndex">
											<i-ball-modal :tag="cgIndexId" />
										</template>
										<template #MB_STRATEGY v-if="isMultipleIndex">
											<i-ball-modal tag="MB_STRATEGY" />
										</template>
									</sticky-text-wrap>
								</th>
							</tr>
							<tr class="row-container">
								<td class="com-mod-table-data align-left" :colspan="!isMultipleIndex ? '2' : '1'">
									<sticky-text-wrap
										:text-content="[{ noWrap: true, content: ['R-squared', { slotName: 'RSQ_AFRIBall' }] }]"
									>
										<template #RSQ_AFRIBall>
											<i-ball-modal tag="RSQ_AFR" />
										</template>
									</sticky-text-wrap>
								</td>
								<td v-if="isMultipleIndex" class="com-mod-table-data align-right">
									<span>{{ $format.formatExtraZero(cgBenchMark.rSquared) }}</span>
								</td>
								<td class="align-right com-mod-table-data">
									<span>{{ $format.formatExtraZero(mstarBenchMark.rSquared) }}</span>
								</td>
							</tr>
							<tr class="row-container">
								<td class="com-mod-table-data align-left" :colspan="!isMultipleIndex ? '2' : '1'">
									<sticky-text-wrap
										:text-content="[{ noWrap: true, content: ['Beta', { slotName: 'BETA_AFIBall' }] }]"
									>
										<template #BETA_AFIBall>
											<i-ball-modal tag="BETA_AF" />
										</template>
									</sticky-text-wrap>
								</td>
								<td v-if="isMultipleIndex" class="com-mod-table-data align-right">
									<span>{{ $format.formatExtraZero(cgBenchMark.beta) }}</span>
								</td>
								<td class="align-right com-mod-table-data">
									<span>{{ $format.formatExtraZero(mstarBenchMark.beta) }}</span>
								</td>
							</tr>
							<tr class="row-container">
								<td class="com-mod-table-data align-left" :colspan="!isMultipleIndex ? '2' : '1'">
									<sticky-text-wrap
										:text-content="[{ content: ['Capture Ratio'] }, { noWrap: true, content: ['(Downside/Upside)', { slotName: 'DCAPRATIBall' }, { slotName: 'UCAPRATIBall' }] }]"
									>
										<template #DCAPRATIBall>
											<i-ball-modal tag="DCAPRAT" />
										</template>
										<template #UCAPRATIBall>
											<i-ball-modal tag="UCAPRAT" />
										</template>
									</sticky-text-wrap>
								</td>
								<td v-if="isMultipleIndex" class="com-mod-table-data align-right">
									<span>{{ $format.formatExtraZero(cgBenchMark.captureRatioDown) }}/{{ $format.formatExtraZero(cgBenchMark.captureRatioUp) }}</span>
								</td>
								<td class="align-right com-mod-table-data">
									<span>{{ $format.formatExtraZero(mstarBenchMark.captureRatioDown) }}/{{ $format.formatExtraZero(mstarBenchMark.captureRatioUp) }}</span>
								</td>
							</tr>
							<tr class="row-container">
								<td class="sub-info adjust-date" colspan="3">
									<span
										v-if="isMultipleIndex"
									>American Funds Benchmark for the {{ mstarBenchMark.mStarRiskMeasureYear || '--' }} Years ending {{ $format.shortDate(cgBenchMark.riskMeasuresAsOfDate) }} (updated monthly).</span>
									<br>
									<span>Morningstar Benchmark for the {{ mstarBenchMark.mStarRiskMeasureYear || '--' }} Years ending {{ $format.shortDate(mstarBenchMark.asOfDate) }} (updated monthly).</span>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { mapGetters } from 'vuex';
export default {
	name: 'RiskMeasuresModule',
	computed: {
		...mapGetters(['getCGBenchmark', 'getMstarBenchmark']),
		cgBenchMark() {
			return this.getCGBenchmark || {};
		},
		cgIndexId() {
			return this.cgBenchMark.indexID;
		},
		mstarBenchMark() {
			return this.getMstarBenchmark || {};
		},
		isMultipleIndex() {
			return (
				this.cgBenchMark &&
				this.cgBenchMark.IndexFundShareClassID !==
					this.mstarBenchMark.primaryBenchmarkID
			);
		},
		columnHeader() {
			return this.cgBenchMark &&
				this.cgBenchMark.IndexFundShareClassID ===
					this.mstarBenchMark.primaryBenchmarkID
				? 'Insurance Series/Morningstar Benchmark'
				: 'Morningstar Benchmark';
		},
		cgBenchMarkName() {
			//formatted so the sticky wrap component binds the iballs and disclosures with the last word, or the '--' if there is no index name
			const name = this.cgBenchMark.indexName || '';
			return [
				name ? name.substring(0, name.lastIndexOf(' ')) : name,
				name ? name.split(' ').pop() : '--'
			];
		},
		mstarBenchMarkkName() {
			//formatted so the sticky wrap component binds the iballs and disclosures with the last word, or the '--' if there is no index name
			const name = this.mstarBenchMark.primaryBenchmarkName || '';
			return [
				name ? name.substring(0, name.lastIndexOf(' ')) : name,
				name ? name.split(' ').pop() : '--'
			];
		}
	},
	watch: {
		getCGBenchmark: {
			immediate: true,
			handler(val) {
				this.updateExcelData(val, null);
			}
		},
		getMstarBenchmark: {
			immediate: true,
			handler(val) {
				if (val) {
					this.$store.commit('updateIBallDescriptions', {
						MB_STRATEGY: val.mStarBenchmarkStrategy
					});
				}
				this.updateExcelData(null, val);
			}
		}
	},
	methods: {
		updateExcelData(cgBenchMark, mstarBenchMark) {
			if (!cgBenchMark) cgBenchMark = this.cgBenchMark || {};
			if (!mstarBenchMark) mstarBenchMark = this.mstarBenchMark || {};
			this.$EventBus.$emit('ExcelData', 'RiskMeasuresModule', {
				MB_STRATEGY: mstarBenchMark.mStarBenchmarkStrategy,
				standardDeviation: this.$format.formatExtraZero(
					mstarBenchMark.standardDeviation
				),
				standardDeviationAsOfDate: `For the ${mstarBenchMark.mStarRiskMeasureYear ||
					'--'} Years ending ${this.$format.shortDate(
					mstarBenchMark.asOfDate
				)} (updated monthly).`,
				americanFundsBenchmarkAsOfDate: `American Funds Benchmark for the ${mstarBenchMark.mStarRiskMeasureYear ||
					'--'} Years ending ${this.$format.shortDate(
					cgBenchMark.riskMeasuresAsOfDate
				)} (updated monthly).`,
				morningstarBenchmarkAsOfDate: `Morningstar Benchmark for the ${mstarBenchMark.mStarRiskMeasureYear ||
					'--'} Years ending ${this.$format.shortDate(
					mstarBenchMark.asOfDate
				)} (updated monthly).`,
				sharpeRatio: this.$format.formatExtraZero(
					mstarBenchMark.sharpeRatio
				),
				americanFundsBenchMark: {
					index: cgBenchMark.indexName,
					rSquared: this.$format.formatExtraZero(
						cgBenchMark.rSquared
					),
					beta: this.$format.formatExtraZero(cgBenchMark.beta),
					captureRatioDown: this.$format.formatExtraZero(
						cgBenchMark.captureRatioDown
					),
					captureRatioUp: this.$format.formatExtraZero(
						cgBenchMark.captureRatioUp
					)
				},
				morningstarBenchmark: {
					index: mstarBenchMark.primaryBenchmarkName,
					rSquared: this.$format.formatExtraZero(
						mstarBenchMark.rSquared
					),
					beta: this.$format.formatExtraZero(mstarBenchMark.beta),
					captureRatioDown: this.$format.formatExtraZero(
						mstarBenchMark.captureRatioDown
					),
					captureRatioUp: this.$format.formatExtraZero(
						mstarBenchMark.captureRatioUp
					)
				},
				isMultipleIndex:
					cgBenchMark.IndexFundShareClassID !==
					mstarBenchMark.primaryBenchmarkID,
				columnHeader:
					cgBenchMark.IndexFundShareClassID ===
					mstarBenchMark.primaryBenchmarkID
						? 'Insurance Series/Morningstar Benchmark'
						: 'Morningstar Benchmark'
			});
		}
	}
};
</script>

<style lang="less" scoped>
.risk-measures {
	width: 100%;
	table {
		table-layout: fixed;
	}
	.row-container:nth-child(9) {
		white-space: nowrap;
	}
}
.row-container {
	height: 24px;
	border-bottom: 1px solid #ccc;
}
.align-right {
	text-align: right;
}
.align-left {
	text-align: left;
}
</style>

